import { parse, isValid, isAfter, isEqual, isBefore, startOfDay, format, addYears } from "date-fns";
import z from "zod";

import { UnloadingDirectionDTOTariffStation } from "@/common/api";
import { locale } from "@/common/locale";
import {
  validateDate,
  validateDatetime,
  validateEmptyDatetime,
  validatePastDate,
  validatePastDatetime,
  validateDateNotMoreThanYearInFuture,
  validateDatetimeNotMoreThanYearInFuture,
  DATETIME_FORMAT,
  DATE_FORMAT,
  preprocessNumber,
} from "@/common/model";

const MAX_INT = 9999 as const;
const MAX_FLOAT = 999999.99 as const;

export const minDateToday = format(startOfDay(new Date()), DATE_FORMAT);
export const maxDateOneYearFromNow = format(startOfDay(addYears(new Date(), 1)), DATE_FORMAT);

const datetimeRequiredSchema = z
  .string({ required_error: locale.required })
  .trim()
  .min(1, { message: locale.required })
  .refine(validateDatetime, {
    message: locale.datetimeFormat,
  });

export const orderFormSchema = z
  .object({
    // Инфо
    customerName: z.string(),
    customerInn: z.string(),
    orderNumber: z.string(),
    createdDate: z.string(),
    // График подачи вагонов
    deadlineAt: z
      .string()
      .trim()
      .min(1, { message: locale.required })
      .refine(validateDate, {
        message: locale.dateFormat,
      })
      .refine(validatePastDate, {
        message: locale.dateInThePast,
      })
      .refine(validateDateNotMoreThanYearInFuture, {
        message: locale.dateMoreThanYearInFuture,
      }),
    deadlineTo: z
      .string()
      .trim()
      .min(1, { message: locale.required })
      .refine(validateDate, {
        message: locale.dateFormat,
      })
      .refine(validatePastDate, {
        message: locale.dateInThePast,
      })
      .refine(validateDateNotMoreThanYearInFuture, {
        message: locale.dateMoreThanYearInFuture,
      }),
    decade: z.boolean().optional(),
    decadeFirst: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .int({ message: locale.notInteger })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`)
        .optional(),
    ),
    decadeSecond: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .int({ message: locale.notInteger })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`)
        .optional(),
    ),
    decadeThird: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .int({ message: locale.notInteger })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`)
        .optional(),
    ),
    decadeComments: z.string().max(500, locale.maxLength(500)).optional(),
    // Торги
    tradeBegin: z
      .string({ required_error: locale.required })
      .trim()
      .min(1, { message: locale.required })
      .refine(validateDatetime, {
        message: locale.datetimeFormat,
      })
      .refine(validatePastDatetime, { message: locale.dateTimeInThePast })
      .refine(validateDatetimeNotMoreThanYearInFuture, {
        message: locale.datetimeMoreThanYearInFuture,
      }),
    tradeEnd: z
      .string({ required_error: locale.required })
      .trim()
      .min(1, { message: locale.required })
      .refine(validateDatetime, {
        message: locale.datetimeFormat,
      })
      .refine(validatePastDatetime, { message: locale.dateTimeInThePast })
      .refine(validateDatetimeNotMoreThanYearInFuture, {
        message: locale.datetimeMoreThanYearInFuture,
      }),
    collectionBegin: datetimeRequiredSchema,
    collectionEnd: z
      .string({ required_error: locale.required })
      .trim()
      .min(1, { message: locale.required })
      .refine(validateDatetime, {
        message: locale.datetimeFormat,
      })
      .refine(validatePastDatetime, { message: locale.dateTimeInThePast }),
    retrading: z.boolean(),
    retradingBegin: z.string().trim().refine(validateEmptyDatetime, {
      message: locale.datetimeFormat,
    }),
    retradingEnd: z
      .string()
      .trim()
      .refine(validateEmptyDatetime, {
        message: locale.datetimeFormat,
      })
      .refine(validatePastDatetime, { message: locale.dateTimeInThePast }),
    resultsDate: datetimeRequiredSchema
      .refine(validatePastDatetime, { message: locale.dateTimeInThePast })
      .refine(validateDatetimeNotMoreThanYearInFuture, {
        message: locale.datetimeMoreThanYearInFuture,
      }),
    // Параметры груза
    cargoId: z.string().min(1, locale.required),
    cargoCode: z.string().optional(),
    cargoWeight: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .int({ message: locale.notInteger })
        .min(1, `${locale.minValue} 1`)
        .max(999_999_999, `${locale.maxValue} 999999999`),
    ),
    wagonType: z.string().min(1, locale.required),
    wagonCode: z.string().optional(),
    wagonCarrying: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_FLOAT, `${locale.maxValue} ${MAX_FLOAT}`)
        .optional(),
    ),
    wagonVolume: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_FLOAT, `${locale.maxValue} ${MAX_FLOAT}`)
        .optional(),
    ),
    cargoComments: z.string().trim().max(500, locale.maxLength(500)),
    // Графики и нормативы
    standartTimeLoading: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .int({ message: locale.notInteger })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`)
        .optional(),
    ),
    standartTimeUnloading: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .int({ message: locale.notInteger })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`)
        .optional(),
    ),
    penalty: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .min(0, `${locale.minValue} 0`)
        .max(MAX_FLOAT, `${locale.maxValue} ${MAX_FLOAT}`)
        .optional(),
    ),
    moratorium: z.boolean(),
    // Направление перевозки
    loadingStation: z.string().min(1, locale.required),
    loadingRoad: z.string(),
    totalWagons: preprocessNumber(
      z.coerce
        .number({ invalid_type_error: locale.notValid })
        .int({ message: locale.notInteger })
        .min(1, `${locale.minValue} 1`)
        .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`),
    ),
    codeStation: z.string().optional(),
    unloadingDirection: z
      .array(
        z.object({
          unloadingStation: z.string(),
          unloadingRoad: z.string().min(1, locale.required),
          countWagons: preprocessNumber(
            z.coerce
              .number({ invalid_type_error: locale.notValid })
              .int({ message: locale.notInteger })
              .min(0, `${locale.minValue} 0`)
              .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`)
              .optional(),
          ),
          tariffStation: z.nativeEnum(UnloadingDirectionDTOTariffStation, {
            message: locale.required,
          }),
          roadCode: z.string().optional(),
          stationCode: z.string().optional(),
        }),
      )
      .min(1, locale.required),
  })
  .superRefine((data, ctx) => {
    const parseDatetime = (value: string) => parse(value, DATETIME_FORMAT, new Date());
    const parseDate = (value: string) => parse(value, DATE_FORMAT, new Date());

    const deadlineAtDate = data.deadlineAt ? parseDate(data.deadlineAt) : null;
    const deadlineToDate = data.deadlineTo ? parseDate(data.deadlineTo) : null;
    const tradeBeginDate = data.tradeBegin ? parseDatetime(data.tradeBegin) : null;
    const tradeEndDate = data.tradeEnd ? parseDatetime(data.tradeEnd) : null;
    const collectionBeginDate = data.collectionBegin ? parseDatetime(data.collectionBegin) : null;
    const collectionEndDate = data.collectionEnd ? parseDatetime(data.collectionEnd) : null;
    const retradingEndDate = data.retradingEnd ? parseDatetime(data.retradingEnd) : null;
    const resultsDate = data.resultsDate ? parseDatetime(data.resultsDate) : null;
    const previousStageEnd = data.retrading ? retradingEndDate : collectionEndDate;

    if (resultsDate && isValid(resultsDate)) {
      if (
        previousStageEnd &&
        isValid(previousStageEnd) &&
        isBefore(resultsDate, previousStageEnd)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.resultsBeforePreviousStage,
          path: ["resultsDate"],
        });
      }
      if (tradeEndDate && isValid(tradeEndDate) && !isEqual(resultsDate, tradeEndDate)) {
        for (const field of ["resultsDate", "tradeEnd"]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: locale.resultsMustMatchTradeEnd,
            path: [field],
          });
        }
      }
    }

    // Валидация deadlineAt: deadlineAt должен быть не раньше tradeEnd (по дате)
    if (deadlineAtDate && tradeEndDate && isValid(deadlineAtDate) && isValid(tradeEndDate)) {
      const deadlineAtStart = startOfDay(deadlineAtDate);
      const tradeEndStart = startOfDay(tradeEndDate);

      if (isBefore(deadlineAtStart, tradeEndStart)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.deadlineAtBeforeTradeEnd,
          path: ["deadlineAt"],
        });
      }
    }

    // Валидация deadlineTo: deadlineTo должна быть строго больше deadlineAt
    if (data.deadlineAt && data.deadlineTo && deadlineAtDate && deadlineToDate) {
      if (isValid(deadlineAtDate) && isValid(deadlineToDate)) {
        const deadlineAtStart = startOfDay(deadlineAtDate);
        const deadlineToStart = startOfDay(deadlineToDate);

        if (!isAfter(deadlineToStart, deadlineAtStart)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: locale.deadlineToOutOfRange,
            path: ["deadlineTo"],
          });
        }
      }
    }

    // Валидация tradeEnd > tradeBegin
    if (
      tradeBeginDate &&
      tradeEndDate &&
      isValid(tradeBeginDate) &&
      isValid(tradeEndDate) &&
      (isAfter(tradeBeginDate, tradeEndDate) || isEqual(tradeBeginDate, tradeEndDate))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: locale.dateEndBeforeStart,
        path: ["tradeEnd"],
      });
    }

    // Сбор предложений должен завершиться внутри сессии; минимальной длительности итогов нет.
    if (
      collectionEndDate &&
      isValid(collectionEndDate) &&
      collectionBeginDate &&
      isValid(collectionBeginDate) &&
      tradeEndDate &&
      isValid(tradeEndDate)
    ) {
      if (
        isBefore(collectionEndDate, collectionBeginDate) ||
        isAfter(collectionEndDate, tradeEndDate)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.collectionEndOutOfRange,
          path: ["collectionEnd"],
        });
      }
    }

    // Валидация retradingEnd: обязательно, если включена переторжка
    if (data.retrading) {
      if (!data.retradingEnd || !data.retradingEnd.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.required,
          path: ["retradingEnd"],
        });
      }
    }

    // Переторжка должна завершиться после сбора предложений и не позже конца сессии.
    if (
      data.retrading &&
      retradingEndDate &&
      isValid(retradingEndDate) &&
      collectionEndDate &&
      isValid(collectionEndDate) &&
      tradeEndDate &&
      isValid(tradeEndDate)
    ) {
      if (
        isBefore(retradingEndDate, collectionEndDate) ||
        isAfter(retradingEndDate, tradeEndDate)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.retradingEndOutOfRange,
          path: ["retradingEnd"],
        });
      }
    }

    // Валидация суммы декад: сумма decadeFirst, decadeSecond, decadeThird не должна превышать totalWagons
    if (data.totalWagons !== undefined && data.totalWagons !== null) {
      const decadeFirst = data.decadeFirst ?? 0;
      const decadeSecond = data.decadeSecond ?? 0;
      const decadeThird = data.decadeThird ?? 0;
      const decadeSum = decadeFirst + decadeSecond + decadeThird;

      if (decadeSum > data.totalWagons) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.decadeSumExceedsTotal,
          path: ["decadeFirst"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.decadeSumExceedsTotal,
          path: ["decadeSecond"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.decadeSumExceedsTotal,
          path: ["decadeThird"],
        });
      }
    }

    // Валидация суммы направлений выгрузки: сумма countWagons по всем unloadingDirection не должна превышать totalWagons
    if (data.totalWagons !== undefined && data.totalWagons !== null) {
      const totalUnloadingWagons = data.unloadingDirection.reduce((sum, direction) => {
        const count = direction.countWagons ?? 0;
        return sum + (Number.isFinite(count) ? count : 0);
      }, 0);

      if (totalUnloadingWagons > data.totalWagons) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.unloadingDirectionSumExceedsTotal,
          path: ["totalWagons"],
        });
      }
    }
  });
